'use client'

import React, { useRef, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import { Link, usePathname } from '@/i18n/routing'
import type { Footer, Media } from '@/payload-types'
import AnimatedFeatures from '@/components/AnimatedFeatures'
import { useTheme } from '@/providers/Theme'
import { useTranslations } from 'next-intl'
import { useAnimation } from '@/providers/Animation'
import { site } from '@/site'
import { GlassSurface } from '@/components/ui/glass'
import styles from './footer.module.css'


const ThemeToggle: React.FC = () => {
  const { theme, setTheme } = useTheme()
  const [isDark, setIsDark] = React.useState(false)

  React.useEffect(() => {
    if (theme === 'dark') setIsDark(true)
    else if (theme === 'light') setIsDark(false)
    else if (typeof window !== 'undefined') setIsDark(window.matchMedia('(prefers-color-scheme: dark)').matches)
  }, [theme])

  return (
    <button
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={`flex items-center justify-between min-h-11 px-3.5 py-2.5 rounded-lg border text-sm font-medium transition-all duration-300 ${
        isDark
          ? 'border-foreground/40 bg-foreground/10 text-foreground hover:bg-foreground/15'
          : 'border-foreground/15 bg-foreground/5 text-muted-foreground hover:bg-foreground/10 hover:text-foreground'
      }`}
    >
      <div className="flex items-center gap-2.5">
        <span className="text-base leading-none opacity-90">
          {isDark ? '🌙' : '☀️'}
        </span>
        <span>{isDark ? 'Dark Mode' : 'Light Mode'}</span>
      </div>
      <div
        className={`w-8 h-4.5 rounded-full relative transition-colors duration-300 ${isDark ? 'bg-foreground/70' : 'bg-foreground/20'}`}
      >
        <div
          className={`absolute top-[2px] w-3.5 h-3.5 rounded-full bg-background transition-all duration-300 shadow-sm ${isDark ? 'left-[1.125rem]' : 'left-0.5'}`}
        />
      </div>
    </button>
  )
}

/* ─── Settings popover (Theme + Animations) ─────────────────────────── */
const SettingsPopover: React.FC = () => {
  const { animationsEnabled, toggleAnimations } = useAnimation()
  const t = useTranslations('footer')
  const tCommon = useTranslations('common')
  const [open, setOpen] = useState(false)
  // Below md the panel is a full-screen sheet portaled to <body>; at md+ it is an
  // in-place popover anchored to the gear. The portal lets the mobile scrim escape
  // the footer's `relative z-10` stacking context so the sticky header (z-50) no
  // longer paints over it (P7d deferred → P8).
  const [isMobile, setIsMobile] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const sync = () => setIsMobile(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      const inTrigger = ref.current?.contains(target)
      const inPanel = panelRef.current?.contains(target)
      if (!inTrigger && !inPanel) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={ref} className="relative">
      {/* Gear trigger */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Site settings"
        aria-expanded={open}
        className={`inline-flex items-center justify-center size-11 rounded-full border transition-all duration-300 ${
          open
            ? 'bg-foreground/15 border-foreground/40 text-foreground'
            : 'bg-foreground/5 border-foreground/20 text-foreground/70 hover:text-foreground hover:bg-foreground/10 hover:border-foreground/30'
        }`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transition-transform duration-500 ${open ? 'rotate-90' : 'rotate-0'}`}
        >
          <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>

      {/* Shared panel card */}
      {open &&
        (() => {
          const card = (
            <div
              ref={panelRef}
              className="relative z-10 w-[calc(100vw-32px)] max-w-[360px] md:w-64 rounded-2xl border border-border bg-background/95 text-foreground backdrop-blur-xl shadow-[0_20px_40px_rgba(0,0,0,0.3)] p-5 flex flex-col gap-5 md:origin-bottom-right animate-in fade-in zoom-in-95 duration-200"
            >
              {/* Mobile Header */}
              <div className="flex md:hidden items-center justify-between pb-2 border-b border-border">
                <span className="text-xs text-foreground uppercase tracking-widest font-semibold">Settings</span>
                <button
                  onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                  aria-label={tCommon('close')}
                  className="size-11 flex items-center justify-center rounded-full bg-foreground/5 text-foreground/70 hover:bg-foreground/10 hover:text-foreground"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                </button>
              </div>

              {/* Theme */}
              <div className="flex flex-col gap-3">
                <span className="text-caption text-muted-foreground uppercase tracking-[0.12em] font-semibold">
                  {t('theme') || 'Theme'}
                </span>
                <ThemeToggle />
              </div>

              {/* Divider */}
              <div className="h-px bg-border" />

              {/* Animations */}
              <div className="flex flex-col gap-3">
                <span className="text-caption text-muted-foreground uppercase tracking-[0.12em] font-semibold">
                  Animations
                </span>
                <button
                  onClick={toggleAnimations}
                  aria-label={animationsEnabled ? 'Disable animations' : 'Enable animations'}
                  className={`flex items-center justify-between min-h-11 px-3.5 py-2.5 rounded-lg border text-sm font-medium transition-all duration-300 ${
                    animationsEnabled
                      ? 'border-foreground/40 bg-foreground/10 text-foreground hover:bg-foreground/15'
                      : 'border-foreground/15 bg-foreground/5 text-muted-foreground hover:bg-foreground/10 hover:text-foreground'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-base leading-none opacity-90">
                      {animationsEnabled ? '✨' : '🌑'}
                    </span>
                    <span>{animationsEnabled ? 'On' : 'Off'}</span>
                  </div>
                  <div
                    className={`w-8 h-4.5 rounded-full relative transition-colors duration-300 ${animationsEnabled ? 'bg-foreground/70' : 'bg-foreground/20'}`}
                  >
                    <div
                      className={`absolute top-[2px] w-3.5 h-3.5 rounded-full bg-background transition-all duration-300 shadow-sm ${animationsEnabled ? 'left-[1.125rem]' : 'left-0.5'}`}
                    />
                  </div>
                </button>
              </div>
            </div>
          )

          // Mobile: portal a full-screen sheet to <body>. Same z as the header
          // (z-50); portaled to the end of <body>, so it paints above by DOM order
          // — no new z-index layer needed.
          if (isMobile) {
            return createPortal(
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
                {card}
              </div>,
              document.body,
            )
          }

          // Desktop: in-place popover anchored to the gear.
          return <div className="absolute z-50 bottom-14 right-0">{card}</div>
        })()}
    </div>
  )
}

/* ─── Main footer ────────────────────────────────────────────────────── */
export const FooterClient = ({ footer }: { footer: Footer }) => {
  const t = useTranslations('footer')
  const pathname = usePathname()
  const navItems = footer?.navItems || []

  const legalLinks = [
    { href: '/datenschutz', label: t('datenschutz') },
    { href: '/impressum', label: t('impressum') },
    { href: '/agb', label: t('terms') },
    { href: '/faq', label: t('faq') },
    { href: '/cookie-policy', label: t('cookie') },
    { href: '/return-policy', label: t('return') },
    { href: '/disclaimer', label: t('disclaimer') },
    // P2-6 (doc 046): link to the cross-border /de/versand page from the
    // footer so German visitors have a frictionless path to the shipping
    // info that addresses the cross-border concerns flagged by GA4.
    { href: '/versand', label: t('shipping') },
  ]

  const shopLinks = [
    { href: '/shop', label: t('shop') },
    { href: '/categories', label: t('categories') },
    { href: '/autoflowering-samen', label: t('autoflowering') },
    { href: '/cannabis-stecklinge', label: t('stecklinge') },
  ]

  const noHeaderFooterUrls = ['/create-account', '/login', '/recover-password']
  if (noHeaderFooterUrls.includes(pathname)) return null

  return (
    <footer className="mt-auto relative w-full">
      {/* Phase 7d: GlassSurface replaces .glass-card. blur={false} on purpose —
          a backdrop-filter here would (1) spend budget on a full-width layer and
          (2) turn the footer into the containing block for the mobile settings
          sheet (`position: fixed`), trapping it inside the footer. Radius 0 +
          overflow visible so the desktop settings popover is never clipped. */}
      <GlassSurface
        variant="sidebar"
        elevation="flat"
        blur={false}
        className="w-full border-x-0 border-b-0"
        style={{ borderRadius: 0, overflow: 'visible' }}
      >

      <div className={`container mx-auto px-4 ${styles.inner}`}>
        <div className={styles.grid}>
          {/* ── Brand ───────────────────────────────────────────── */}
          <div>
            <Link href="/" className="inline-block">
              <Image
                src="/organic-seeds-logo.png"
                alt="organic-seeds.at Logo"
                width={210}
                height={63}
                className="h-[64px] w-auto invert dark:invert-0"
              />
            </Link>

            <span className={styles.note}>
              <span className={styles.chipDot} aria-hidden="true" />
              {t('brand.cashOnly')}
            </span>

            <div className={styles.contact}>
              <p className={styles.contactLabel}>{t('brand.phoneService')}</p>
              <p className={styles.contactMeta}>{t('brand.openingHours')}</p>
              {site.content.contact.phone && (
                <a
                  href={`tel:${site.content.contact.phone.replace(/[^+\d]/g, '')}`}
                  className={styles.link}
                >
                  {site.content.contact.phone}
                </a>
              )}
              <a href={`mailto:${site.content.contact.email}`} className={styles.link}>
                {site.content.contact.email}
              </a>
            </div>
          </div>

          {/* ── Shop ───────────────────────────────────────────── */}
          <nav aria-label={t('shop')}>
            <h2 className={styles.heading}>{t('shop')}</h2>
            <div className={styles.list}>
              {shopLinks.map((link) => (
                <Link key={link.href} href={link.href} className={styles.link}>
                  {link.label}
                </Link>
              ))}
            </div>
          </nav>

          {/* ── Legal — two columns on desktop, 8 links is too tall for one ── */}
          <nav aria-label={t('legal')}>
            <h2 className={styles.heading}>{t('legal')}</h2>
            <div className={`${styles.list} ${styles.listTwoCol}`}>
              {legalLinks.map((link) => (
                <Link key={link.href} href={link.href} className={styles.link}>
                  {link.label}
                </Link>
              ))}
            </div>
          </nav>

          {/* ── Footer global links + newsletter + settings ─────── */}
          <div>
            <h2 className={styles.heading}>{t('followUs')}</h2>
            <div className={styles.list}>
              {navItems.map((item) => {
                const icon = item?.icon as Media
                return (
                  <Link
                    key={item.id}
                    href={item.link.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={item.link.label}
                    className={styles.link}
                  >
                    {icon?.url ? (
                      <Image
                        src={icon.url}
                        alt=""
                        width={28}
                        height={28}
                        className={styles.pillIcon}
                      />
                    ) : (
                      item.link.label
                    )}
                  </Link>
                )
              })}
            </div>

            <div className={styles.actions}>
              <Link href="/newsletter" className={styles.pill}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
                {t('newsletter') || 'Newsletter'}
              </Link>
              <SettingsPopover />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar — copyright moved here from the brand column */}
      <div className={styles.bottom}>
        <div className={`container mx-auto px-4 ${styles.bottomInner}`}>
          {footer?.copyright && <span>{footer.copyright}</span>}
          <span className="flex items-center gap-2">
            <span aria-hidden="true">🇦🇹</span>
            <span>Designed in Austria</span>
          </span>
        </div>
      </div>
      </GlassSurface>
    </footer>
  )
}
